import {
  isDisplayRightsLaunchAllowed,
  resolveDisplayRightsLaunchGate,
  type DisplayRightsLaunchGate,
} from '@/components/display-rights/display-rights-launch-gate';
import { useLaunchMentionBoost } from '@/hooks/actions/use-launch-mention-boost';
import {
  MentionBoostDeliveryState,
  MentionBoostReadinessBlockerCode,
  type DisplayRightsProposalSummary,
  type MentionBoostAdAccountDto,
  type MentionBoostAuthorizationStatus,
  type MentionBoostDto,
  type MentionBoostReadinessResponse,
} from '@fluenceur/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Delivery states a launched boost can be relaunched from. Anything in flight
 * (publishing / active / stopping) or needing attention is NOT relaunchable: a
 * second launch would race the live one.
 */
const RELAUNCHABLE_STATES = new Set<MentionBoostDeliveryState>([
  MentionBoostDeliveryState.FAILED,
  MentionBoostDeliveryState.STOPPED,
]);

/**
 * Whether a launched boost in `state` can be relaunched. Shared with the
 * top-level panel so the editable setup surface and the read-only delivery
 * surface agree on which states still offer a retry path.
 */
export const isBoostRelaunchable = (state: MentionBoostDeliveryState): boolean =>
  RELAUNCHABLE_STATES.has(state);

/**
 * Choosing an account is a configuration act, not a spending one, so a missing
 * payment method does not remove it from the list — only Meta disabling it does.
 * Mirrors the server, which filters the selectable pool on `!disabled` alone and
 * refuses the launch separately when the chosen account cannot pay.
 */
const isSelectable = (account: MentionBoostAdAccountDto): boolean => !account.disabled;

interface UseMentionBoostLaunchOptions {
  mentionId: string;
  // The marketer's explicit choice, owned by `useAdAccountPick` above the setup
  // query so it can reach the server. Null while they have not chosen.
  pickedAdAccountId: string | null;
  // The prepared boost setup must exist before a launch can be offered.
  hasSetup: boolean;
  // Reuses the same eligibility gate the setup form opens behind.
  isEligible: boolean;
  // The prepared setup is valid and settled (the form's `canSave` with no pending
  // load). Folded into `canLaunch` so the CTA, the confirm dialog, and the launch
  // mutation share one gate and cannot diverge while the dialog is open.
  isSetupValid: boolean;
  launchedBoost: MentionBoostDto | null;
  // Ad-account readiness from the setup query. Null when the mention is not
  // launchable, in which case no picker is offered.
  readiness: MentionBoostReadinessResponse | null;
  // The compensation declared on the covering authorization, and where Instagram
  // stands on it. Both null/undefined when the mention carries no authorization
  // yet, which is the state most reads are in.
  displayRights?: DisplayRightsProposalSummary | null;
  authorizationStatus?: MentionBoostAuthorizationStatus | null;
}

interface MentionBoostLaunchState {
  // True when a launched boost occupies the surface (read-only delivery view).
  hasActiveLaunchedBoost: boolean;
  // True when at least one account is offered for selection (enabled at Meta).
  hasSelectableAdAccount: boolean;
  // The marketer may launch (or relaunch) from the setup form. Drives the CTA
  // that opens the confirm panel.
  canLaunch: boolean;
  // The confirm button inside the panel: everything `canLaunch` checks, plus the
  // double validation. Separate on purpose — a marketer blocked by the gate must
  // still be able to open the panel and read which side is missing.
  canConfirmLaunch: boolean;
  isConfirmOpen: boolean;
  isLaunching: boolean;
  // The accounts to offer in the picker: every enabled account, payable or not.
  adAccounts: MentionBoostAdAccountDto[];
  // True when the brand owns >1 selectable account, so the marketer chooses which
  // one is billed from a select at the top of the setup form.
  showAdAccountSelect: boolean;
  // The account that will be billed. Null when the server resolved none and the
  // marketer has not chosen: no account is ever invented here.
  selectedAdAccountId: string | null;
  // The selected account itself, for the surfaces that need more than its id.
  selectedAdAccount: MentionBoostAdAccountDto | null;
  // The selected account cannot spend until a payment method is added in Meta.
  needsPaymentMethod: boolean;
  // The brand still owes the DSA advertiser declaration, so no ad set can be
  // created. Collected in the panel, not in Meta.
  needsDsaBeneficiary: boolean;
  // The selected account bills in another currency. Fluenceur deliberately
  // does not invent an exchange rate for a confirmed boost budget.
  needsCurrencyCompatibleAdAccount: boolean;
  openConfirm: () => void;
  closeConfirm: () => void;
  confirmLaunch: () => void;
  // The double validation the confirm panel mirrors. `applies: false` means no
  // compensation was declared and nothing about the panel changes.
  displayRightsGate: DisplayRightsLaunchGate;
  // The marketer affirming the compensation was agreed outside Fluenceur.
  offPlatformAttestation: boolean;
  setOffPlatformAttestation: (value: boolean) => void;
}

/**
 * Owns the launch side effect for a prepared boost: explicit-confirm dialog
 * state, the ad-account selection, and the launch mutation.
 *
 * The selection is read from the server (`readiness.selectedAdAccountId`) and
 * only overridden by an explicit pick. When the server resolves no account it is
 * withholding one on purpose — several accounts are usable and none is preferred
 * — and the launch stays unavailable until the marketer chooses. Guessing here
 * would silently bill an account the brand never named, and an `adAccountId`
 * sent to the launch outranks the brand's saved default server-side, so the
 * guess would win over the very preference it was meant to stand in for.
 */
export function useMentionBoostLaunch({
  mentionId,
  pickedAdAccountId,
  hasSetup,
  isEligible,
  isSetupValid,
  launchedBoost,
  readiness,
  displayRights = null,
  authorizationStatus = null,
}: UseMentionBoostLaunchOptions): MentionBoostLaunchState {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [offPlatformAttestation, setOffPlatformAttestation] = useState(false);
  const { isPending: isLaunching, mutate: launchBoost } = useLaunchMentionBoost();

  const launchedState = launchedBoost?.deliveryState ?? null;
  const isRelaunchable = launchedState ? isBoostRelaunchable(launchedState) : true;
  const hasActiveLaunchedBoost = !!launchedBoost && !isRelaunchable;

  const adAccounts = useMemo(() => readiness?.adAccounts ?? [], [readiness]);

  // The select is offered only when there is a real choice: more than one
  // selectable account. A single one is resolved server-side and needs no picker.
  const selectableAdAccounts = useMemo(() => adAccounts.filter(isSelectable), [adAccounts]);
  const hasSelectableAdAccount = selectableAdAccounts.length > 0;
  const showAdAccountSelect = selectableAdAccounts.length > 1;

  // A pick only counts while the account it names is still selectable. Showing
  // it immediately, rather than waiting for the readiness resolved against it,
  // keeps the picker responsive; deriving it rather than storing a resolved id
  // is what keeps the selection in step with a readiness that arrives late or
  // changes under the panel, instead of freezing whatever the first render saw.
  const activePick =
    pickedAdAccountId && selectableAdAccounts.some((account) => account.id === pickedAdAccountId)
      ? pickedAdAccountId
      : null;
  const selectedAdAccountId = activePick ?? readiness?.selectedAdAccountId ?? null;
  const selectedAdAccount =
    adAccounts.find((account) => account.id === selectedAdAccountId) ?? null;
  // The account picker updates before its account-scoped readiness query has
  // returned. Never let a ready response for the previous account authorize the
  // newly selected one.
  const hasReadySelectedAccount =
    readiness?.ready === true && readiness.selectedAdAccountId === selectedAdAccountId;

  // Both refusals are read off the server's own blockers, resolved against the
  // account named in the request, so the panel never re-derives a verdict the
  // server already reached. It mirrors them only so the CTA stops inviting a
  // launch already known to come back refused.
  const hasBlocker = useCallback(
    (code: MentionBoostReadinessBlockerCode) =>
      !!readiness?.blockers.some((blocker) => blocker.code === code),
    [readiness]
  );
  const needsPaymentMethod = hasBlocker(
    MentionBoostReadinessBlockerCode.AD_ACCOUNT_PAYMENT_MISSING
  );
  const needsDsaBeneficiary = hasBlocker(MentionBoostReadinessBlockerCode.DSA_BENEFICIARY_MISSING);
  const needsCurrencyCompatibleAdAccount = hasBlocker(
    MentionBoostReadinessBlockerCode.AD_ACCOUNT_CURRENCY_MISMATCH
  );

  // Where the double validation stands. Recomputed on render rather than stored:
  // it is a pure reading of data the panel already holds, and a stored copy would
  // go stale the moment the creator answers.
  const displayRightsGate = useMemo(
    () => resolveDisplayRightsLaunchGate(displayRights, authorizationStatus),
    [displayRights, authorizationStatus]
  );

  // A tick made against one version says nothing about the next: replacing the
  // terms (or the creator answering) drops it, so the marketer affirms the
  // agreement they are actually looking at.
  useEffect(() => {
    setOffPlatformAttestation(false);
  }, [displayRights?.id]);

  // The single launch gate. It folds in every condition the CTA used to check
  // separately (a settled ad-account selection, a valid settled setup) so the
  // confirm dialog and the mutation cannot submit a launch the CTA would now
  // block. `!isLaunching` and `!hasActiveLaunchedBoost` drop the CTA the instant
  // a launch fires and keep it gone once a boost is in flight, never overlapping
  // the read-only view.
  //
  // The display rights gate is NOT folded into it: the CTA has to stay clickable
  // so the marketer can open the panel and read WHICH side is missing. The
  // confirm button inside the panel is what the gate disables.
  const canLaunch =
    hasSetup &&
    isEligible &&
    isSetupValid &&
    isRelaunchable &&
    hasReadySelectedAccount &&
    !!selectedAdAccountId &&
    !needsPaymentMethod &&
    !needsDsaBeneficiary &&
    !needsCurrencyCompatibleAdAccount &&
    !isLaunching &&
    !hasActiveLaunchedBoost;

  const canConfirmLaunch =
    canLaunch && isDisplayRightsLaunchAllowed(displayRightsGate, offPlatformAttestation);

  const openConfirm = useCallback(() => setIsConfirmOpen(true), []);
  const closeConfirm = useCallback(() => setIsConfirmOpen(false), []);

  const confirmLaunch = useCallback(() => {
    if (!canConfirmLaunch) return;
    launchBoost(
      {
        mentionId,
        // Sent only on an explicit pick. An id sent here outranks the brand's
        // saved default server-side, so echoing back a server-resolved account
        // would turn a preference the brand can change into one this panel
        // freezes.
        adAccountId: activePick ?? undefined,
        // Sent only when the marketer ticked it: the server stamps the
        // affirmation, and an unticked launch must never carry one implicitly.
        offPlatformAttestation: offPlatformAttestation || undefined,
      },
      { onSuccess: () => setIsConfirmOpen(false) }
    );
  }, [activePick, canConfirmLaunch, launchBoost, mentionId, offPlatformAttestation]);

  return {
    hasActiveLaunchedBoost,
    hasSelectableAdAccount,
    canLaunch,
    canConfirmLaunch,
    isConfirmOpen,
    isLaunching,
    adAccounts: selectableAdAccounts,
    showAdAccountSelect,
    selectedAdAccountId,
    selectedAdAccount,
    needsPaymentMethod,
    needsDsaBeneficiary,
    needsCurrencyCompatibleAdAccount,
    openConfirm,
    closeConfirm,
    confirmLaunch,
    displayRightsGate,
    offPlatformAttestation,
    setOffPlatformAttestation,
  };
}
